import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import * as R from 'ramda'
import { Defaults, type Nil, RoleName, useFields } from '@pbt/pbt-ui-components'

import { ConversationTransport } from '~/api/graphql/generated/types'
import {
  CreatedConversationFlow,
  type CreatedConversationsInfoDialogProps,
} from '~/components/dashboard/communications/common/CreatedConversationsInfoDialog'
import type { ConversationMessageFormattingAreaHandle } from '~/components/dashboard/communications/ConversationMessageFormattingArea'
import {
  ConversationCategory,
  ConversationStatus,
  INITIAL_COMMUNICATION_FILTERS,
  MessageStatus,
  MessageType,
  MessageWay,
} from '~/constants/communications'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { BaseRoute } from '~/constants/routes'
import { EasterEggEvents, type TableFilter } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'
import useHasRole from '~/utils/useHasRole'

import type { RootState } from '..'
import {
  closeMessageFormattingArea,
  messageFormattingAreaFocused,
  sendConversationDraftMessage,
  sendConversationMessage,
  updateMessage,
} from '../actions/conversationMessages'
import {
  fetchClientConversationsList,
  fetchConversationsList,
  fetchMoreItemsForClientConversationsList,
  fetchMoreItemsForConversationsList,
  setConversationsListFilters,
  setIsReadForConversation,
  toggleArchiveForConversation,
} from '../actions/conversations'
import { queueEasterEggEvent } from '../actions/easterEgg'
import { getConstantsIsLoading, getFeatureToggle } from '../reducers/constants'
import {
  getAttachmentsToSend,
  getEditMessage,
  getMessage,
  getMessageFormattingAreaExpanded,
} from '../reducers/conversationMessages'
import {
  getAreConversationsCreated,
  getConversationById,
  getConversationsError,
  getConversationsListFilters,
} from '../reducers/conversations'
import { getTimetableEvent } from '../reducers/timetable'
import { useCanSendSmsToClient } from './clients'

export function useConversationList({
  clientId,
  patientId,
  queryFrom,
  queryTo,
  fillPrecedingWithNull,
}: {
  clientId?: string | Nil
  fillPrecedingWithNull?: boolean
  patientId?: string | Nil
  queryFrom?: number
  queryTo?: number
}) {
  const dispatch = useDispatch()

  const filters = useSelector(getConversationsListFilters)
  const isConstantsLoading = useSelector(getConstantsIsLoading)
  const isSuppressAllSystemMessagesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SUPPRESS_ALL_SYSTEM_MESSAGES),
  )

  const applyFilter = (filterName: string, filterValue: TableFilter) => {
    dispatch(
      setConversationsListFilters(
        filterValue.value
          ? { ...filters, [filterName]: filterValue }
          : R.omit([filterName], filters),
      ),
    )
  }

  const clearFilters = () => {
    const initialFilters: { [key: string]: { value: any } } =
      INITIAL_COMMUNICATION_FILTERS

    if (isSuppressAllSystemMessagesEnabled) {
      initialFilters.excludeSystemMessages = { value: true }
    }

    dispatch(setConversationsListFilters(initialFilters))
  }

  useEffect(() => {
    const initialFilters: { [key: string]: { value: any } } =
      isSuppressAllSystemMessagesEnabled
        ? {
            ...INITIAL_COMMUNICATION_FILTERS,
            excludeSystemMessages: { value: true },
          }
        : INITIAL_COMMUNICATION_FILTERS

    dispatch(
      setConversationsListFilters({
        ...initialFilters,
        ...filters,
        category: {
          value: filters.category?.value || ConversationCategory.INBOX,
        },
        patientIds: { value: null },
      }),
    )
  }, [isSuppressAllSystemMessagesEnabled])

  useEffect(() => {
    const action = clientId
      ? // TODO: add endpoint for fetching client/patient conversations based on ? patientId
        fetchClientConversationsList(
          clientId,
          queryFrom || 0,
          queryTo || Defaults.CONVERSATIONS_BATCH_LOAD_COUNT,
          fillPrecedingWithNull,
        )
      : fetchConversationsList(
          queryFrom || 0,
          queryTo || Defaults.CONVERSATIONS_BATCH_LOAD_COUNT,
          fillPrecedingWithNull,
        )
    dispatch(action)
  }, [filters, clientId, isConstantsLoading])

  const loadMoreItems = (startIndex: number, stopIndex?: number) => {
    // loadMoreItems has trouble recognizing unloaded items at the start of the list
    // so if we're asking for a startIndex close to 0, we start from 0
    const offset = startIndex < 10 ? 0 : startIndex
    // need to calculate limit based on offset and stopIndex
    const limit = stopIndex
      ? stopIndex - offset + 1
      : Defaults.CONVERSATIONS_BATCH_LOAD_COUNT
    const action = clientId
      ? fetchMoreItemsForClientConversationsList(clientId, offset, limit)
      : fetchMoreItemsForConversationsList(offset, limit)
    dispatch(action)
  }

  return { loadMoreItems, clearFilters, applyFilter, filters }
}

export function useCreatedConversationsInfo({
  getIsConversationCreating,
  onConversationCreationSuccess,
  onConversationCreationFailure,
  onViewConversation = R.F,
  displayDefaultSuccessResult = true,
  createdInfoDialogProps = {},
}: {
  createdInfoDialogProps?: Partial<CreatedConversationsInfoDialogProps>
  displayDefaultSuccessResult?: boolean
  getIsConversationCreating: (state: RootState) => boolean
  onConversationCreationFailure?: (error: string) => void
  onConversationCreationSuccess?: () => void
  onViewConversation?: (conversationId: string) => void
}) {
  const navigate = useNavigate()
  const areConversationsCreated = useSelector(getAreConversationsCreated)
  const latestConversationError = useSelector(getConversationsError)
  const [openCreatedConversationsInfoDialog] = useDialog(
    DialogNames.CREATED_CONVERSATIONS_INFO_DIALOG,
  )

  const handleViewConversation = (conversationId: string) => {
    navigate(`${BaseRoute.COMMUNICATIONS}/${conversationId}`)
    onViewConversation(conversationId)
  }

  const conversationCreationSuccess = () => {
    if (displayDefaultSuccessResult) {
      openCreatedConversationsInfoDialog({
        ...createdInfoDialogProps,
        flow: CreatedConversationFlow.PLAIN_MESSAGE_SENT,
        handleViewConversation,
      })
    }

    if (onConversationCreationSuccess) {
      onConversationCreationSuccess()
    }
  }

  const conversationCreationFail = () => {
    if (onConversationCreationFailure) {
      onConversationCreationFailure(latestConversationError as string)
    }
  }

  const onConversationCreated = () => {
    if (areConversationsCreated) {
      conversationCreationSuccess()
    } else {
      conversationCreationFail()
    }
  }

  return useCloseAfterCreation(onConversationCreated, getIsConversationCreating)
}

export function useIncludeMedicalRecord() {
  const [medicalValidationError, setMedicalValidationError] = useState<
    string | undefined
  >()
  const isMarkMessagesAsMedicalEnabled = useSelector(
    getFeatureToggle(FeatureToggle.MARK_MESSAGES_AS_MEDICAL),
  )
  const isVeterinarian1 = useHasRole(RoleName.Veterinarian)
  const isVeterinarian2 = useHasRole(RoleName.Veterinarian2)
  const isVeterinarian = isVeterinarian1 || isVeterinarian2

  const isVetTech = useHasRole(RoleName.VetTech)
  const isVetAssistant = useHasRole(RoleName.VetAssistant)
  const isVetTechOrAssistant = isVetTech || isVetAssistant

  return {
    isMarkMessagesAsMedicalEnabled,
    shouldShowCheckbox:
      isMarkMessagesAsMedicalEnabled &&
      (isVeterinarian || isVetTechOrAssistant),
    defaultCheckboxValue: isMarkMessagesAsMedicalEnabled && isVeterinarian,
    medicalValidationError,
    setMedicalValidationError,
  }
}

export const useMessageFormatting = ({
  conversationId,
}: {
  conversationId: string
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isCommunicationRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
  )
  const isCvcSystemMessageSuppressionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_SYSTEM_MESSAGE_SUPPRESSION),
  )
  const isLogicalSMSThreadingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.LOGICAL_SMS_THREADING),
  )
  const { t } = useTranslation(['Common', 'Communications', 'Clients'])

  const editMessage = useSelector(getEditMessage)
  const conversation = useSelector(getConversationById(conversationId))
  const files = useSelector(getAttachmentsToSend)
  const messageFormattingAreaExpanded = useSelector(
    getMessageFormattingAreaExpanded,
  )
  const appointment = useSelector(getTimetableEvent(conversation?.eventId))

  const conversationFormattingAreaRef =
    useRef<ConversationMessageFormattingAreaHandle>(null)

  const location = useLocation()
  const [openDialog] = useDialog(DialogNames.CONVERSATION_INTERNAL_NOTE)
  const [openNewThreadReplyDialog] = useDialog(DialogNames.NEW_THREAD_REPLY)

  const isLoading = !conversation
  const {
    state: status,
    transport,
    client: clientId,
    patient: patientId,
  } = conversation || {}

  const isSystemMessage =
    isCvcSystemMessageSuppressionEnabled &&
    Boolean(conversation?.isSystemMessage)

  const canSendSmsToClient = useCanSendSmsToClient(clientId)

  const {
    isMarkMessagesAsMedicalEnabled,
    shouldShowCheckbox,
    defaultCheckboxValue,
    medicalValidationError,
    setMedicalValidationError,
  } = useIncludeMedicalRecord()

  const {
    fields: { messageField, isMedical },
    validate,
    reset,
  } = useFields([
    {
      name: 'messageField',
      label: t('Common:MESSAGE'),
      validators: ['required'],
      initialValue: editMessage?.body ?? '',
    },
    {
      name: 'isMedical',
      label: t('Communications:MEDICAL_RECORDS.INCLUDE_MESSAGE'),
      initialValue: editMessage?.isMedical ?? defaultCheckboxValue,
    },
  ])

  const isDeliveryFailed = status === ConversationStatus.DELIVERY_FAILED
  const isSmsTransport = transport === ConversationTransport.Sms
  const internalNoteOnly = transport === ConversationTransport.LogPhoneCall
  const canSendMessage = isSmsTransport ? canSendSmsToClient : !internalNoteOnly
  const showSendButton =
    messageFormattingAreaExpanded || !isMarkMessagesAsMedicalEnabled
  const showReplyButton =
    (isMarkMessagesAsMedicalEnabled || isLogicalSMSThreadingEnabled) &&
    !messageFormattingAreaExpanded &&
    !internalNoteOnly

  const latestMessageId = conversation?.messages?.[0]
  const latestMessage = useSelector(getMessage(latestMessageId))
  const isLatestMessageInbound = latestMessage?.way === MessageWay.INBOUND

  const showReplyInNewThreadButton =
    isLogicalSMSThreadingEnabled &&
    (conversation?.totalCount ?? 0) > 1 &&
    Boolean(latestMessage) &&
    isLatestMessageInbound

  const archivedActionTitle = isLogicalSMSThreadingEnabled
    ? t('Communications:MESSAGE_FORMATTING_AREA.ARCHIVE_THREAD')
    : t('Communications:MESSAGE_FORMATTING_AREA.ARCHIVE_CONVERSATION')

  const openActionTitle = isLogicalSMSThreadingEnabled
    ? t('Communications:MESSAGE_FORMATTING_AREA.OPEN_THREAD')
    : t('Common:OPEN_CONVERSATION')
  const archiveActionTitle =
    status === ConversationStatus.ARCHIVED
      ? openActionTitle
      : archivedActionTitle

  const goBack = () => navigate(-1)

  const onInternalNoteAdd = () => openDialog({ conversationId })

  const onArchiveAction = () => {
    if (status !== ConversationStatus.ARCHIVED) {
      dispatch(
        queueEasterEggEvent({
          actionType: EasterEggEvents.ARCHIVE_CONVERSATION,
        }),
      )
    }
    dispatch(toggleArchiveForConversation(conversationId))
  }

  const onValidate = (isDraft: boolean) => {
    if (isDraft && isMedical.value) {
      setMedicalValidationError(
        t('Communications:MEDICAL_RECORDS.CANNOT_SAVE_AS_DRAFT'),
      )
      return false
    }

    setMedicalValidationError(undefined)
    return validate()
  }

  const handleMessageSend = () => {
    if (onValidate(false)) {
      if (editMessage) {
        dispatch(
          updateMessage({
            messageId: editMessage.id,
            status: MessageStatus.SENT,
            text: messageField.value,
            files,
            isMedical: isMedical.value,
          }),
        )
      } else {
        dispatch(
          sendConversationMessage({
            conversationId,
            messageType: MessageType.PLAIN,
            body: messageField.value,
            files,
            isMedical: isMedical.value,
          }),
        )
      }
      reset()
    }
  }

  const handleMessageDraftSend = () => {
    if (onValidate(true)) {
      if (editMessage) {
        dispatch(
          updateMessage({
            messageId: editMessage.id,
            status: MessageStatus.DRAFT,
            text: messageField.value,
            files,
            isMedical: isMedical.value,
          }),
        )
      } else {
        dispatch(
          sendConversationDraftMessage({
            conversationId,
            messageType: MessageType.PLAIN,
            body: messageField.value,
            isMedical: isMedical.value,
          }),
        )
      }
      reset()
    }
  }

  const onMarkUnread = () => {
    dispatch(setIsReadForConversation(conversationId, false))
    goBack()
  }

  const handleCloseMessageFormattingArea = () => {
    dispatch(closeMessageFormattingArea())
  }

  const handleReplyClick = () => {
    dispatch(messageFormattingAreaFocused())
  }

  const handleReplyInNewThreadClick = () => {
    if (!latestMessage) return
    openNewThreadReplyDialog({
      conversation: conversation!,
      messageId: latestMessage.id,
    })
  }

  const setMessageText = (text: string | Nil) => {
    messageField.setValue(text)
    conversationFormattingAreaRef.current?.resetMessageState()
  }

  useEffect(() => {
    setMessageText(editMessage?.body)
  }, [editMessage?.body])

  useEffect(() => {
    if (!R.isNil(editMessage?.isMedical)) {
      isMedical.setValue(editMessage.isMedical)
    } else {
      isMedical.setValue(defaultCheckboxValue)
    }
  }, [defaultCheckboxValue, editMessage?.isMedical])

  useEffect(() => {
    const openFromNewThreadReply = (
      location.state as { openMessageFormattingArea?: boolean } | null
    )?.openMessageFormattingArea
    if (openFromNewThreadReply) {
      dispatch(messageFormattingAreaFocused())
      navigate(location.pathname, { replace: true, state: {} })
    } else {
      handleCloseMessageFormattingArea()
    }
  }, [])

  return {
    messageFormattingAreaExpanded,
    handleCloseMessageFormattingArea,
    canSendMessage,
    clientId,
    conversation,
    isCommunicationRightRailEnabled,
    messageField,
    patientId,
    appointment,
    conversationFormattingAreaRef,
    transport,
    isMarkMessagesAsMedicalEnabled,
    dispatch,
    internalNoteOnly,
    showSendButton,
    isLoading,
    handleMessageSend,
    showReplyButton,
    handleReplyClick,
    showReplyInNewThreadButton,
    handleReplyInNewThreadClick,
    handleMessageDraftSend,
    shouldShowCheckbox,
    isMedical,
    onInternalNoteAdd,
    isDeliveryFailed,
    onMarkUnread,
    onArchiveAction,
    archiveActionTitle,
    isSystemMessage,
    messageError: medicalValidationError,
  }
}
